package com.dynamicruntime.webapp

import com.dynamicruntime.common.home.HFRAG
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.aside
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.main
import react.dom.html.ReactHTML.nav
import react.dom.html.ReactHTML.p
import react.useEffect
import react.useState
import web.cssom.ClassName

/** Coroutine scope for firing the suspend home calls from React effects/handlers. */
private val homeScope = MainScope()

/**
 * The home page — assembled from data rather than hardcoded. It fetches its UI-config (the "construction
 * manifest") and builds itself from it:
 *  - Its **copy** comes from the `home` Markdown fragment file the config names (nothing here is a literal);
 *  - Its **layout** comes from the config's feature flags — the document links may be shown as a top menu
 *    bar, a left nav bar, inline in the body, any combination, or not at all;
 *  - Its **links** come from the config's state, each naming a Markdown document to open.
 *
 * Selecting a link goes to that document's own page ([DocsPage], issue #554) -- Home no longer renders a
 * document in place, so the listing and every document are destinations with history entries of their own.
 */
val Home = FC<Props> {
    var config by useState<HomeConfig?>(null)
    var copy by useState(Copy.empty)
    var error by useState<DisplayError?>(null)

    val generation = useRefreshGeneration()
    // The copy's links, as a document's are (issue #1510): `/docs/...` to the in-app document, `/wa/...` under the live
    // app root, so the intro can point to "Start here" as the public home's sections point to the walk-through.
    val copyLinks = useShellFacts().copyLinkResolver()

    // Re-read the home config on every refresh generation (issue #115): on mount, and whenever a navigation or
    // state mutation bumps it.
    // And on every idle poll: it reads the home config, a polled endpoint, and static copy alone (issue #1372).
    val polls = useIdlePolls()
    useEffect(generation, polls) {
        homeScope.launch {
            try {
                val loaded = HomeApi.fetchConfig()
                config = loaded
                // Recover a stale build id silently (issue #469): a rolling deploy leaves this ref one deploy
                // old, which 404s -- re-fetch the config for a fresh ref and retry once, rather than surfacing
                // the misleading "is the runtime running?" below for a runtime that is running perfectly.
                copy = fetchCopyWithRetry(loaded.fragment) {
                    apiResult { HomeApi.fetchConfig().fragment }.valueOrNull()
                }
                error = null
            } catch (e: Throwable) {
                error = userFacingError(e)
            }
        }
    }
    val current = config
    val layout = current?.layout
    // Only the documents the listing offers; a suite's inner pages are reached by links (issue #1136).
    val links = current?.links?.filter { it.listed } ?: emptyList()

    div {
        className = ClassName("home-shell")

        // Presentation 1: the links as a horizontal menu bar above the content.
        if (layout?.topBar == true && links.isNotEmpty()) {
            nav {
                className = ClassName("home-topbar")
                docLinks(links)
            }
        }

        div {
            className = ClassName("home-body")

            // Presentation 2: the links as a left nav bar beside the content.
            if (layout?.leftBar == true && links.isNotEmpty()) {
                aside {
                    className = ClassName("home-leftbar")
                    copy.opt("nav", "title")?.let {
                        h2 { +it }
                    }
                    docLinks(links)
                }
            }

            main {
                className = ClassName("home-main")
                when {
                    error != null -> errorText("Couldn't load this page.", error!!)
                    // The welcome page: copy from the fragment file, and optionally the links inline.
                    current != null -> {
                        // On a test instance, for a caller with test access (issue #1183): becoming someone first.
                        if (appConfig().hasTestAccess) TestAccessCard {}
                        // The hero: the brand mark beside the wordmark. The wordmark is "copy" like everything
                        // else here, so a deployment that names no brand simply gets no hero.
                        copy.opt(HFRAG.homeNs, HFRAG.brand)?.let { brandName ->
                            div {
                                className = ClassName("home-hero")
                                img {
                                    className = ClassName("home-hero-mark")
                                    // The client's own mark where it has set one (issue #1130), as the bar draws.
                                    src = markUrlFor(config)
                                    // Decorative: the wordmark beside it carries the name.
                                    alt = ""
                                }
                                div {
                                    className = ClassName("home-hero-name")
                                    +brandName
                                }
                            }
                        }
                        copy.opt(HFRAG.homeNs, HFRAG.title)?.let { h1 { +it } }
                        copy.opt(HFRAG.homeNs, HFRAG.intro)?.let { Markdown { source = it; linkResolver = copyLinks } }
                        // A note beneath the intro (issue #1221), empty as shipped: room for a component's own word on
                        // a deployment -- a flavor's, say -- without its competing with whoever rewords the intro.
                        copy.opt(HFRAG.homeNs, HFRAG.note)?.takeIf { it.isNotBlank() }?.let {
                            div {
                                className = ClassName("home-note")
                                Markdown { source = it; linkResolver = copyLinks }
                            }
                        }
                        // The public home page (issue #1324): for someone signed in to `public` -- never a visitor who
                        // has not signed in, though they act in `public` too -- what they can do next.
                        if (isPublicHome(current.user)) {
                            PublicHome {
                                // Qualified: Home's own `config` and `copy` are state, and a bare name here would set
                                // them -- during render, which loops.
                                this.config = current
                                this.copy = copy
                            }
                        }
                        if (layout?.inlineLinks == true) {
                            renderInlineLinks(links, copy)
                        }
                    }
                    else -> p {
                        className = ClassName("subtitle")
                        +"Loading…"
                    }
                }
            }
        }
    }
}

/** The links as inline body content (the third presentation). */
private fun ChildrenBuilder.renderInlineLinks(links: List<HomeLink>, copy: Copy) {
    copy.opt("nav", "title")?.let { h2 { +it } }
    if (links.isEmpty()) {
        p {
            className = ClassName("type-hint")
            +copy.t("nav", "emptyNote", "")
        }
        return
    }
    div {
        className = ClassName("home-inline-links")
        docLinks(links)
    }
}
